/**
 * Live state for the connector catalog: which systems carry data
 * (`source_registry`) and which have a firm connection (`cloud_accounts`).
 *
 * Two reads, two failure modes, on purpose. HAS DATA comes from the registry
 * and earns a tick; IS CONNECTED comes from `GET /integrations/connections`
 * and removes the Connect button, because connecting is once per firm and a
 * second attempt returns 409. A registry outage must never decide whether
 * Connect is offered, so the connections read fails OPEN on its own.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { DataSource } from "../config/dataSources";
import { dataSourcesService } from "../services/dataSourcesService";
import { listConnectedProviders } from "../services/integrationApi";
import type { RegisteredSource } from "../types/dataSource";
import { indexBySourceSystem } from "../utils/sourceStatus";

export interface DataSourceStatus {
	registered: RegisteredSource[];
	/** Registry rows keyed by lower-cased `source_system`. */
	bySystem: Record<string, RegisteredSource>;
	/** One instant every row's relative age is measured from. */
	now: number;
	/** True once the registry read has settled, success or not. */
	loaded: boolean;
	/** Connected and not awaiting re-authorization: Connect is not offered. */
	isLocked: (dataSource: DataSource) => boolean;
	needsReauthorization: (dataSource: DataSource) => boolean;
}

/** Refetches each time `active` turns true, so a reopened dialog is current. */
export function useDataSourceStatus(active: boolean): DataSourceStatus {
	const [registered, setRegistered] = useState<RegisteredSource[]>([]);
	const [loaded, setLoaded] = useState(false);
	const [connected, setConnected] = useState<Set<string>>(() => new Set());
	const [reauth, setReauth] = useState<Set<string>>(() => new Set());
	const [now, setNow] = useState(() => Date.now());
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (!active) return;
		let cancelled = false;
		const live = () => !cancelled && isMountedRef.current;
		void (async () => {
			try {
				const rows = await dataSourcesService.listSources();
				if (live()) {
					setRegistered(rows);
					setNow(Date.now());
				}
			} catch {
				// Non-fatal: the catalog renders without ticks.
				if (live()) setRegistered([]);
			} finally {
				if (live()) setLoaded(true);
			}
		})();
		void (async () => {
			try {
				const status = await listConnectedProviders();
				if (live()) {
					setConnected(new Set(status.connectedProviders));
					setReauth(new Set(status.reauthorizationRequiredProviders));
				}
			} catch {
				// Fail OPEN: first connect is the onboarding critical path, and a
				// redundant attempt costs a legible 409.
				if (live()) {
					setConnected(new Set());
					setReauth(new Set());
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [active]);

	const needsReauthorization = useCallback(
		(dataSource: DataSource) =>
			Boolean(dataSource.cloudProvider && reauth.has(dataSource.cloudProvider)),
		[reauth],
	);

	const isLocked = useCallback(
		(dataSource: DataSource) =>
			Boolean(
				dataSource.cloudProvider &&
					connected.has(dataSource.cloudProvider) &&
					!reauth.has(dataSource.cloudProvider),
			),
		[connected, reauth],
	);

	return {
		registered,
		bySystem: indexBySourceSystem(registered),
		now,
		loaded,
		isLocked,
		needsReauthorization,
	};
}
